import React, { useState, useEffect } from "react";
import axios from "axios";
import { config } from "../../Helpers/env";
import { LoadingGif } from "../Utils/Loader";
import PageTitle from "../Utils/smsTitle";
import { SubHeader } from "../../Containers/SubHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faDollarSign,
  faShoppingCart,
  faPiggyBank,
  faArrowUp,
  faArrowDown,
  faSyncAlt
} from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { BackButton } from "../Utils/Buttons/DataExportationButton";
import { Container } from "../Utils/styledComponents";

const KPICard = ({ title, sspValue, usdValue, icon, gradient }) => (
  <div className="col-6 col-md-3">
    <div
      className="card border-0 shadow-sm text-white h-100"
      style={{
        background: gradient,
        borderRadius: "10px"
      }}
    >
      <div className="card-body text-center py-3">
        <FontAwesomeIcon icon={icon} className="mb-2 opacity-75" />
        <h6 className="mb-1 opacity-75" style={{ fontSize: "12px" }}>
          {title}
        </h6>
        <h3 className="mb-0 fw-bold" style={{ fontSize: "1.25rem", color: "#fff" }}>
          {sspValue}
        </h3>
        <div style={{ fontSize: "13px", opacity: 0.9 }}>{usdValue}</div>
      </div>
    </div>
  </div>
);

const BusinessInsightDashboard = () => {
  const [filter, setFilter] = useState({
    start_date: format(new Date(), "yyyy-MM-dd"),
    end_date: format(new Date(), "yyyy-MM-dd")
  });
  const [period, setPeriod] = useState({ ...filter });
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, chartRes] = await Promise.all([
        axios.get(`${config.smsUrl}/creport/insight_report`, { params: period }),
        axios.get(`${config.smsUrl}/creport/insight_graph_data`, {
          params: period
        })
      ]);
      setSummary(summaryRes.data);
      setChartData(chartRes.data.chart_data || []);
    } catch (err) {
      console.error("Failed to load dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  const handleFilter = () => {
    setPeriod({ ...filter });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <LoadingGif />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="text-center py-5 text-danger">Failed to load data</div>
    );
  }

  const { revenue, expenses, profit } = summary;
  const isProfit = profit.status === "PROFIT";

  return (
    <>
      <PageTitle title="Business Insight Dashboard" />
      <SubHeader
        title="CEO Dashboard"
        subTitle="Real-Time Financial Intelligence"
      >
        <FontAwesomeIcon icon={faChartLine} />
      </SubHeader>

      <Container className="mt-1">
        {/* ===== TOP SECTION ===== */}
        <div style={{ padding: "8px 0 0 0" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: "12px",
              gap: "8px"
            }}
          >
            <BackButton to="/reports/generalreports" text="Reports" />

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "10px"
              }}
            >
              <input
                type="date"
                className="form-control form-control-sm"
                value={filter.start_date}
                onChange={(e) =>
                  setFilter({ ...filter, start_date: e.target.value })
                }
                style={{
                  width: "145px",
                  height: "32px",
                  padding: "2px 8px",
                  fontSize: "13px",
                  borderRadius: "6px"
                }}
              />
              <input
                type="date"
                className="form-control form-control-sm"
                value={filter.end_date}
                onChange={(e) =>
                  setFilter({ ...filter, end_date: e.target.value })
                }
                style={{
                  width: "145px",
                  height: "32px",
                  padding: "2px 8px",
                  fontSize: "13px",
                  borderRadius: "6px"
                }}
              />
              <button
                type="button"
                onClick={handleFilter}
                className="btn btn-sm text-white"
                style={{
                  backgroundColor: "#389081",
                  borderColor: "#389081",
                  height: "32px",
                  padding: "0 14px",
                  fontSize: "13px",
                  borderRadius: "6px"
                }}
              >
                <FontAwesomeIcon
                  icon={faSyncAlt}
                  spin={loading}
                  className="me-1"
                />
                Apply Filter
              </button>
            </div>
          </div>

          <div
            style={{
              fontSize: "12px",
              color: "#6c757d",
              marginBottom: "14px"
            }}
          >
            Updated: {summary.generated_at}
          </div>
        </div>

        {/* KPI CARDS – system colors */}
        <div className="row g-3 mb-4">
          <KPICard
            title="Revenue"
            sspValue={`SSP ${Number(revenue.ssp || 0).toLocaleString()}`}
            usdValue={`$${Number(revenue.usd || 0).toLocaleString()}`}
            icon={faDollarSign}
            gradient="linear-gradient(135deg, #389081, #027a5f)"
          />
          <KPICard
            title="Expenses"
            sspValue={`SSP ${Number(expenses.ssp || 0).toLocaleString()}`}
            usdValue={`$${Number(expenses.usd || 0).toLocaleString()}`}
            icon={faShoppingCart}
            gradient="linear-gradient(135deg, #F76B1D, #e05a0d)"
          />
          <KPICard
            title={`Net ${profit.status}`}
            sspValue={`SSP ${Number(profit.ssp || 0).toLocaleString()}`}
            usdValue={`$${Number(profit.usd || 0).toLocaleString()}`}
            icon={isProfit ? faArrowUp : faArrowDown}
            gradient={
              isProfit
                ? "linear-gradient(135deg, #08819c, #0a6b7a)"
                : "linear-gradient(135deg, #c0392b, #e74c3c)"
            }
          />
          <KPICard
            title="Next Month Target"
            sspValue={`SSP ${Number((revenue.ssp || 0) * 1.15).toLocaleString()}`}
            usdValue="+15% Growth"
            icon={faPiggyBank}
            gradient="linear-gradient(135deg, #3e3e3e, #2b2a2a)"
          />
        </div>

        {/* CHARTS */}
        <div className="row g-3 mb-4">
          <div className="col-lg-6">
            <div
              className="card border-0 shadow-sm h-100"
              style={{ borderRadius: "10px" }}
            >
              <div
                className="card-header text-white py-2 text-center"
                style={{
                  backgroundColor: "#389081",
                  borderRadius: "10px 10px 0 0",
                  fontSize: "14px",
                  fontWeight: 600
                }}
              >
                SSP Trend
              </div>
              <div className="card-body p-3">
                {chartData.length === 0 ? (
                  <div className="text-center py-5 text-muted">No SSP data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(v) => `SSP ${Number(v).toLocaleString()}`}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="revenue_ssp"
                        stroke="#389081"
                        strokeWidth={2.5}
                        name="Revenue"
                      />
                      <Line
                        type="monotone"
                        dataKey="expense_ssp"
                        stroke="#e05a0d"
                        strokeWidth={2.5}
                        name="Expense"
                      />
                      <Line
                        type="monotone"
                        dataKey="profit_ssp"
                        stroke="#08819c"
                        strokeWidth={2.5}
                        name="Profit"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div
              className="card border-0 shadow-sm h-100"
              style={{ borderRadius: "10px" }}
            >
              <div
                className="card-header text-white py-2 text-center"
                style={{
                  backgroundColor: "#08819c",
                  borderRadius: "10px 10px 0 0",
                  fontSize: "14px",
                  fontWeight: 600
                }}
              >
                USD Trend
              </div>
              <div className="card-body p-3">
                {chartData.length === 0 ? (
                  <div className="text-center py-5 text-muted">No USD data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(v) => `$${Number(v).toLocaleString()}`}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="revenue_usd"
                        stroke="#389081"
                        strokeWidth={2.5}
                        name="Revenue"
                      />
                      <Line
                        type="monotone"
                        dataKey="expense_usd"
                        stroke="#e05a0d"
                        strokeWidth={2.5}
                        name="Expense"
                      />
                      <Line
                        type="monotone"
                        dataKey="profit_usd"
                        stroke="#08819c"
                        strokeWidth={2.5}
                        name="Profit"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* PIE + MESSAGE */}
        <div className="row g-3">
          <div className="col-lg-4">
            <div
              className="card border-0 shadow-sm h-100"
              style={{ borderRadius: "10px" }}
            >
              <div
                className="card-header py-2"
                style={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "10px 10px 0 0",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#3e3e3e"
                }}
              >
                Profit Distribution (SSP)
              </div>
              <div className="card-body p-3">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Profit", value: Math.max(profit.ssp, 0) },
                        { name: "Expenses", value: expenses.ssp }
                      ]}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      dataKey="value"
                    >
                      <Cell fill="#389081" />
                      <Cell fill="#e05a0d" />
                    </Pie>
                    <Tooltip
                      formatter={(v) => `SSP ${Number(v).toLocaleString()}`}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="col-lg-8">
            <div
              className="card border-0 shadow-sm h-100 d-flex align-items-center justify-content-center text-center"
              style={{
                borderRadius: "10px",
                backgroundColor: "#2b2a2a",
                color: "#fff",
                minHeight: "280px"
              }}
            >
              <div className="p-4">
                <h2
                  className="fw-bold mb-2"
                  style={{
                    fontSize: "1.8rem",
                    color: isProfit ? "#fff" : "#fff"
                  }}
                >
                  {isProfit ? "YOU ARE WINNING BOSS!" : "TIME TO DOMINATE!"}
                </h2>
                <p style={{ fontSize: "15px", opacity: 0.85, margin: 0 }}>
                  Your empire is growing. Keep crushing it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default BusinessInsightDashboard;